import React, { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, X, AlertTriangle, Check, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { uploads, checks, apiCustomers } from '../../lib/api';
import CustomerCombobox from '../forms/CustomerCombobox';
import { toast } from 'sonner';

const money = (n) => '$' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// The screen between a scanned cheque and the ledger.
//
// Every figure here is editable, and none of it is recorded until someone
// presses Record. The reading is a starting point: it is faster to correct one
// field than to type six, and far cheaper than unpicking a payment applied to
// the wrong account. The split is shown line by line, and it must add up to the
// cheque before the button will do anything.
export default function CheckScanner({ onClose, onRecorded }) {
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState([]);
  const [attachmentIds, setAttachmentIds] = useState([]);
  const [scan, setScan] = useState(null);
  const [form, setForm] = useState(null);
  const previews = useRef([]);

  useEffect(() => { apiCustomers.list().then((c) => setCustomers(c || [])).catch(() => {}); }, []);
  useEffect(() => () => { previews.current.forEach((u) => URL.revokeObjectURL(u)); }, []);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4);
    e.target.value = '';
    if (!files.length) return;
    const urls = files.map((f) => { const u = URL.createObjectURL(f); previews.current.push(u); return u; });
    setPreview(urls); setScan(null); setForm(null); setBusy('upload');
    try {
      const res = await uploads.upload(files, { kind: 'check', linkedType: 'payment', note: 'cheque scan' });
      const ids = (res?.files || []).map((f) => f.id);
      setAttachmentIds(ids);
      setBusy('read');
      const proposal = await checks.scan(ids);
      setScan(proposal);
      setForm({
        customerId: proposal.customer?.id || '',
        amount: proposal.check?.amount ?? '',
        date: proposal.check?.date || '',
        checkNumber: proposal.check?.checkNumber || '',
        payer: proposal.check?.payer || '',
        memo: proposal.check?.memo || '',
        allocations: (proposal.allocation || []).map((a) => ({ ...a })),
        unapplied: proposal.unapplied || 0,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not read that scan');
      setPreview([]);
    } finally { setBusy(''); }
  };

  // Re-splitting on the client keeps the arithmetic honest while someone edits:
  // the server checks it again, but seeing the credit move as you type is what
  // stops a wrong figure being confirmed in the first place.
  const setAllocation = (invoiceId, value) => {
    const next = form.allocations.map((a) =>
      a.invoiceId === invoiceId ? { ...a, apply: value === '' ? '' : Number(value) } : a);
    const applied = next.reduce((s, a) => s + (Number(a.apply) || 0), 0);
    setForm({ ...form, allocations: next, unapplied: Math.round((Number(form.amount) - applied) * 100) / 100 });
  };

  const setAmount = (value) => {
    const amount = value === '' ? '' : Number(value);
    const applied = form.allocations.reduce((s, a) => s + (Number(a.apply) || 0), 0);
    setForm({ ...form, amount, unapplied: Math.round(((Number(amount) || 0) - applied) * 100) / 100 });
  };

  const applied = form ? form.allocations.reduce((s, a) => s + (Number(a.apply) || 0), 0) : 0;
  const balanced = form && Math.abs(applied + (Number(form.unapplied) || 0) - (Number(form.amount) || 0)) < 0.01;
  const overApplied = form && form.unapplied < -0.005;
  const canRecord = form && form.customerId && Number(form.amount) > 0 && balanced && !overApplied && !busy;

  const record = async () => {
    setBusy('record');
    try {
      const res = await checks.apply({
        attachmentIds,
        customerId: form.customerId,
        amount: Number(form.amount),
        date: form.date || null,
        checkNumber: form.checkNumber,
        payer: form.payer,
        memo: form.memo,
        accountLast4: scan?.check?.accountLast4 || '',
        allocations: form.allocations
          .filter((a) => Number(a.apply) > 0)
          .map((a) => ({ invoiceId: a.invoiceId, apply: Number(a.apply) })),
        unapplied: Number(form.unapplied) || 0,
      });
      toast.success(res.alreadyRecorded ? 'Already recorded — nothing changed' : `${money(res.applied)} recorded`);
      onRecorded && onRecorded();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record that cheque — nothing was changed');
    } finally { setBusy(''); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="check-scanner-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><Camera className="w-5 h-5 text-[#8BC53F]" />Scan a check</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <label className="inline-flex items-center gap-1.5 px-4 h-10 bg-white border border-slate-200 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer">
              {busy === 'upload' || busy === 'read' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              {busy === 'read' ? 'Reading the check…' : busy === 'upload' ? 'Uploading…' : 'Choose or photograph a check'}
              <input type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={onPick} data-testid="check-file-input" />
            </label>
            <p className="text-xs text-slate-500 mt-2">Front is enough. Add the back too if you want it kept with the payment. JPEG or PNG.</p>
            {preview.length > 0 && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {preview.map((u, i) => <img key={i} src={u} alt="Check scan" className="h-24 rounded border border-slate-200" />)}
              </div>
            )}
          </div>

          {form && (
            <>
              {scan?.notes?.length > 0 && (
                <Card className={`p-4 ${scan.needsReview ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`} data-testid="check-scan-notes">
                  <div className="text-xs font-bold flex items-center gap-1.5 text-amber-900">
                    {scan.needsReview ? <AlertTriangle className="w-3.5 h-3.5" /> : <Info className="w-3.5 h-3.5" />}
                    Check these against the paper
                  </div>
                  <ul className="mt-1.5 space-y-1 text-xs text-amber-900 list-disc pl-5">
                    {scan.notes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </Card>
              )}

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Customer</Label>
                  <CustomerCombobox
                    customers={customers}
                    value={form.customerId}
                    onSelect={(c) => setForm({ ...form, customerId: c.id, allocations: [], unapplied: Number(form.amount) || 0 })}
                    placeholder="Search customers…"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    {scan?.matchReason ? `Matched because ${scan.matchReason}.` : ''}
                    {scan?.matchConfidence === 'low' ? ' Worth confirming.' : ''}
                  </p>
                </div>
                <div><Label className="text-xs">Amount</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" data-testid="check-amount" /></div>
                <div><Label className="text-xs">Date</Label>
                  <Input type="date" value={form.date || ''} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Check number</Label>
                  <Input value={form.checkNumber} onChange={(e) => setForm({ ...form, checkNumber: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Payer</Label>
                  <Input value={form.payer} onChange={(e) => setForm({ ...form, payer: e.target.value })} className="mt-1" /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Memo</Label>
                  <Input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} className="mt-1" /></div>
              </div>

              <div>
                <h4 className="font-bold text-slate-900 text-sm">How it will be applied</h4>
                {form.allocations.length === 0 ? (
                  <p className="text-sm text-slate-500 mt-1">
                    No open invoices to apply this to — the whole amount will sit as a credit on the account.
                  </p>
                ) : (
                  <table className="w-full mt-2 text-sm">
                    <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                      <th className="py-1.5">Invoice</th><th>Date</th><th className="text-right">Owed</th><th className="text-right w-32">Apply</th>
                    </tr></thead>
                    <tbody>
                      {form.allocations.map((a) => (
                        <tr key={a.invoiceId} className="border-b border-slate-100">
                          <td className="py-2 font-semibold text-slate-800">{a.invoiceNumber || a.invoiceId}</td>
                          <td className="text-slate-500 text-xs">{a.invoiceDate || '—'}</td>
                          <td className="text-right tabular-nums text-slate-600">{money(a.outstanding)}</td>
                          <td className="text-right">
                            <Input type="number" step="0.01" value={a.apply}
                              onChange={(e) => setAllocation(a.invoiceId, e.target.value)}
                              className="h-8 text-right tabular-nums" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="flex justify-between items-center mt-3 text-sm">
                  <span className="text-slate-600">Applied {money(applied)}
                    {(Number(form.unapplied) || 0) > 0.005 && <> · <span className="text-amber-700 font-semibold">{money(form.unapplied)} left as a credit</span></>}
                  </span>
                  <span className="font-bold tabular-nums text-slate-900">Check {money(form.amount)}</span>
                </div>
                {overApplied && (
                  <p className="text-xs text-red-600 mt-1 font-semibold">
                    That applies {money(-form.unapplied)} more than the check is for.
                  </p>
                )}
              </div>

              <div className="flex gap-2 pt-1">
                <Button onClick={record} disabled={!canRecord} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="check-record-btn">
                  {busy === 'record' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Record this check
                </Button>
                <Button variant="outline" onClick={onClose}>Cancel</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
